/**
 * Document Import Type Definitions
 * Types for document upload, parsing, account resolution, and transaction review
 */

// ============================================================================
// Staged Import Types
// ============================================================================

export type StagedImportStatus =
  | 'pending_account'  // Waiting for account resolution
  | 'pending_review'   // Account resolved, waiting for transaction review
  | 'ready'            // Ready to import
  | 'importing'        // Currently importing
  | 'completed'        // Import finished
  | 'failed';          // Import failed

export interface ParsedAccountInfo {
  institution: string;
  accountType: string;
  accountMask: string;
  statementStart: string;
  statementEnd: string;
}

// ============================================================================
// Multi-Account Resolution Types (WP-DOC-10)
// ============================================================================

/**
 * Parsed account from a document with suggestions and resolution status
 */
export interface ParsedAccount {
  id: string;                          // Unique identifier for this parsed account
  institution: string;                 // Institution name (e.g., "Chase")
  accountType: string;                 // Account type (e.g., "checking", "credit_card")
  accountMask: string;                 // Last 4 digits (e.g., "1234")
  suggestedMatchId: string | null;     // Best suggested existing account ID
  confidence: number;                  // Confidence score for the suggested match (0-1)
  statementStart?: string;             // Statement start date
  statementEnd?: string;               // Statement end date
  transactionCount?: number;           // Number of transactions for this account
}

/**
 * User's resolution action for a parsed account
 */
export type AccountResolutionAction = 'link' | 'create' | 'skip';

/**
 * Resolution state for a single parsed account
 */
export interface AccountResolution {
  parsedAccountId: string;             // ID of the parsed account being resolved
  action: AccountResolutionAction;     // User's chosen action
  linkedAccountId?: string;            // Existing account ID (when action is 'link')
  newAccountData?: {                   // New account data (when action is 'create')
    name: string;
    type: string;
    subtype?: string;
    institution?: string;
    mask?: string;
  };
}

/**
 * Extended staged import with multi-account support
 */
export interface MultiAccountStagedImport extends StagedImport {
  parsedAccounts?: ParsedAccount[];    // Multiple accounts from the document
  accountResolutions?: AccountResolution[];  // User's resolutions for each account
}

export interface AccountSuggestion {
  accountId: string;
  accountName: string;
  accountMask?: string;
  accountType: string;
  confidence: number;
  matchReasons: string[];
}

export interface StagedImport {
  id: string;
  documentId: string;
  status: StagedImportStatus;
  parsedAccount: ParsedAccountInfo;
  accountSuggestions: AccountSuggestion[];
  confirmedAccountId: string | null;
  observationCount: number;
  /** True if document contains only balance info with no transactions (WP-BUG-20) */
  isBalanceOnly: boolean;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Transaction Observation Types
// ============================================================================

export type ObservationMatchStatus = 'new' | 'duplicate' | 'needs_review';

export type ObservationUserAction = 'import' | 'skip' | 'keep_both' | 'replace' | null;

export interface MatchedTransaction {
  id: string;
  date: string;
  amount: number;
  description: string;
  source: string;
}

export interface TransactionObservation {
  id: string;
  stagedImportId: string;
  observedDate: string;
  observedAmount: number;
  observedDescription: string;
  observedBalance: number | null;
  observedCategory: string | null;
  matchStatus: ObservationMatchStatus;
  matchedTransaction: MatchedTransaction | null;
  matchConfidence: number | null;
  matchReasons: string[];
  userAction: ObservationUserAction;

  // Category suggestion fields (WP-DOC-26)
  /** Suggested category UUID from LLM extraction */
  suggestedCategoryId: string | null;
  /** Suggested category name for display */
  suggestedCategoryName: string | null;
  /** Confidence of category suggestion (0-1) */
  categoryConfidence: number | null;

  // User override fields (WP-DOC-26)
  /** User-selected category (overrides suggestion) */
  userCategoryId: string | null;
  /** User-selected category name for display */
  userCategoryName: string | null;

  // Computed fields for display (WP-DOC-26)
  /** Final category: userCategoryId ?? suggestedCategoryId */
  effectiveCategoryId: string | null;
  /** Final category name for display */
  effectiveCategoryName: string | null;
  /** True if user has overridden the suggested category */
  isUserOverride: boolean;
}

export interface ObservationsSummary {
  new: number;
  duplicate: number;
  needsReview: number;
}

export interface BalanceInfo {
  openingBalance: number | null;
  closingBalance: number | null;
  netChange: number;
  statementClosing: number | null;
  balanceMatches: boolean;
}

export interface ObservationsResponse {
  observations: TransactionObservation[];
  total: number;
  summary: ObservationsSummary;
  balanceInfo: BalanceInfo;
}

// ============================================================================
// Request/Response Types
// ============================================================================

export interface ConfirmAccountRequest {
  accountId?: string;
  createAccount?: {
    institution: string;
    accountType: string;
    accountName: string;
    accountMask?: string;
  };
}

export interface ConfirmAccountResponse {
  stagedImportId: string;
  confirmedAccountId: string;
  status: StagedImportStatus;
}

export interface ResolveObservationRequest {
  action: 'import' | 'skip' | 'keep_both' | 'replace';
  applyToSimilar?: boolean;
}

export interface UpdateObservationRequest {
  observedDate?: string;
  observedAmount?: number;
  observedDescription?: string;
  categoryId?: string;
}

/**
 * Request for updating observation category (WP-DOC-26)
 * Sets user override category or clears it (null reverts to LLM suggestion)
 */
export interface UpdateObservationCategoryRequest {
  /** Category UUID to assign (null to clear override and revert to suggestion) */
  categoryId: string | null;
}

export interface ImportTransactionsRequest {
  observationIds?: string[];
  importAllNew?: boolean;
  skipDuplicates?: boolean;
}

export interface ImportTransactionsResponse {
  importedCount: number;
  skippedCount: number;
  anchorCreated: boolean;
  transactionIds: string[];
}

// ============================================================================
// Document Upload Types
// ============================================================================

/**
 * Document processing status
 * Backend statuses:
 * - ingested: File uploaded, waiting for processing
 * - classifying: AI is classifying the document type
 * - classified: Classification complete, ready for parsing
 * - parsing: Extracting transactions from document
 * - parsed: Parsing complete (legacy)
 * - staged: Ready for user review
 * - committed: Import completed, transactions created
 * - failed: Processing or import failed
 *
 * Frontend-only statuses (for UI):
 * - uploading: File is being uploaded
 * - processing: Generic processing state
 * - pending_account: Waiting for account resolution
 * - pending_review: Waiting for transaction review
 * - completed: Legacy completion status
 * - error: Legacy error status
 */
export type DocumentStatus =
  | 'ingested'
  | 'classifying'
  | 'classified'
  | 'parsing'
  | 'parsed'
  | 'staged'
  | 'committed'
  | 'uploading'
  | 'processing'
  | 'pending_account'
  | 'pending_review'
  | 'completed'
  | 'failed'
  | 'error';

export interface Document {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes?: number;
  status: DocumentStatus;
  errorMessage?: string;
  stagedImportId?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Parameters for listing documents
 */
export interface ListDocumentsParams {
  status?: DocumentStatus;
  limit?: number;
  offset?: number;
}

/**
 * Response from listing documents
 */
export interface ListDocumentsResponse {
  documents: Document[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Status filter options for the documents list UI
 * Maps UI filter to API status values
 */
export type DocumentStatusFilter = 'all' | 'processing' | 'pending_review' | 'completed' | 'failed';

/**
 * Result of documentsService.uploadFile (upload/init + pre-signed POST +
 * upload/complete). Processing is asynchronous: poll the document
 * (useStagedImportByDocumentId) for its staged import (WP-IMPORT-1).
 */
export interface UploadFileResult {
  documentId: string;
}

// ============================================================================
// Pre-signed URL Upload Types (3-step upload pattern)
// ============================================================================

export type DocumentType = 'bank_statement' | 'credit_card' | 'investment' | 'tax' | 'other';

export interface UploadInitRequest {
  filename: string;
  mimeType: string;
  sizeBytes: number;
  documentType?: DocumentType;
  folderPath?: string;
}

export interface UploadInitResponse {
  documentId: string;
  uploadUrl: string;
  uploadFields: Record<string, string>;
  expiresAt: string;
}

export interface UploadCompleteRequest {
  documentId: string;
  etag?: string | null;
}

export interface UploadCompleteResponse {
  document: {
    id: string;
    filename: string;
    mimeType: string;
    sizeBytes: number;
    status: DocumentStatus;
    createdAt: string;
  };
  processingStatus: 'queued' | 'processing' | 'completed' | 'failed';
  taskId: string;
  stagedImportId: string | null;
}

// ============================================================================
// Upload Constants
// ============================================================================

/**
 * Supported MIME types for document upload
 */
export const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/x-ofx',
  'application/x-qfx',
  'application/x-qif',
  'application/json',
  'application/zip',
] as const;

/**
 * File extensions for user-friendly display
 */
export const SUPPORTED_EXTENSIONS = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.heic',
  '.csv',
  '.xls',
  '.xlsx',
  '.ofx',
  '.qfx',
  '.qif',
  '.json',
  '.zip',
] as const;

/**
 * File size limits by subscription tier (in MB)
 */
export const FILE_SIZE_LIMITS = {
  free: 10,
  premium: 50,
} as const;

// ============================================================================
// Wizard State Types
// ============================================================================

export type WizardStep = 'upload' | 'account_resolution' | 'transaction_review' | 'complete';

export interface WizardState {
  currentStep: WizardStep;
  stagedImportIds: string[];
  completedSteps: WizardStep[];
}

// ============================================================================
// Filter Types
// ============================================================================

export type ObservationFilter = 'all' | 'new' | 'duplicate' | 'needs_review';

// ============================================================================
// Competitor Import Types
// ============================================================================

/**
 * Supported competitor apps for import
 */
export type CompetitorApp = 'mint' | 'ynab' | 'quicken';

/**
 * Detected category from a competitor import
 */
export interface CategoryDetection {
  sourceCategory: string;
  transactionCount: number;
  suggestedFlowioCategoryId: string | null;
  suggestedFlowioCategoryName: string | null;
  confidence: number;
}

/**
 * Response from detecting categories in a staged import
 */
export interface DetectedCategoriesResponse {
  stagedImportId: string;
  sourceApp: CompetitorApp | null;
  categories: CategoryDetection[];
  totalTransactions: number;
}

/**
 * Single category mapping from source to FlowIO
 */
export interface CategoryMappingInput {
  sourceCategory: string;
  flowioCategoryId: string | null;
  saveForFuture: boolean;
}

/**
 * Request to apply category mappings
 */
export interface ApplyCategoryMappingsRequest {
  mappings: CategoryMappingInput[];
  sourceApp: CompetitorApp;
}

/**
 * Response after applying category mappings
 */
export interface ApplyCategoryMappingsResponse {
  success: boolean;
  transactionsUpdated: number;
  mappingsSaved: number;
  message: string;
}

/**
 * Saved category mapping for reuse
 */
export interface SavedCategoryMapping {
  id: string;
  sourceApp: CompetitorApp;
  sourceCategory: string;
  sourceSubcategory: string | null;
  targetCategoryId: string | null;
  createdAt: string;
}

/**
 * Response for list of saved mappings
 */
export interface SavedMappingsResponse {
  mappings: SavedCategoryMapping[];
  total: number;
}

// ============================================================================
// Competitor Import Wizard Types
// ============================================================================

/**
 * Steps in the competitor import wizard. Account confirmation, review and the
 * commit happen afterwards in the routed import wizard (WP-IMPORT-1).
 */
export type CompetitorImportStep = 'select_app' | 'upload' | 'processing' | 'category_mapping';

/**
 * State for the competitor import wizard
 */
export interface CompetitorImportWizardState {
  currentStep: CompetitorImportStep;
  sourceApp: CompetitorApp | null;
  stagedImportId: string | null;
  categoryMappings: Map<string, CategoryMappingInput>;
}

// ============================================================================
// Document Counts Types (WP-DOC-17)
// ============================================================================

/**
 * Document counts by status for tab badges
 */
export interface DocumentCounts {
  processing: number;
  pending_review: number;
  completed: number;
  failed: number;
  all: number;
}
